/**
 * FinderSudokuPage — offline-capable Sudoku puzzle for the road.
 *
 * Fully client-side: generates a valid grid, removes cells based on
 * difficulty, and lets the user play in-browser OR print an A4-sized
 * puzzle. No backend calls, works offline (PWA-friendly).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";

// ── Sudoku generator (deterministic seeded shuffle for reproducible print)
function shuffled(arr, rand = Math.random) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function isSafe(grid, r, c, v) {
  for (let i = 0; i < 9; i++) {
    if (grid[r][i] === v || grid[i][c] === v) return false;
  }
  const br = Math.floor(r / 3) * 3;
  const bc = Math.floor(c / 3) * 3;
  for (let i = br; i < br + 3; i++) {
    for (let j = bc; j < bc + 3; j++) {
      if (grid[i][j] === v) return false;
    }
  }
  return true;
}

function fillGrid(grid) {
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      if (grid[r][c] === 0) {
        for (const v of shuffled([1, 2, 3, 4, 5, 6, 7, 8, 9])) {
          if (isSafe(grid, r, c, v)) {
            grid[r][c] = v;
            if (fillGrid(grid)) return true;
            grid[r][c] = 0;
          }
        }
        return false;
      }
    }
  }
  return true;
}

function countSolutions(grid, limit = 2) {
  let count = 0;
  const solve = () => {
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (grid[r][c] === 0) {
          for (let v = 1; v <= 9; v++) {
            if (isSafe(grid, r, c, v)) {
              grid[r][c] = v;
              solve();
              grid[r][c] = 0;
              if (count >= limit) return;
            }
          }
          return;
        }
      }
    }
    count++;
  };
  solve();
  return count;
}

function generatePuzzle(difficulty) {
  const target = { easy: 40, medium: 32, hard: 26 }[difficulty] || 32;
  const solution = Array.from({ length: 9 }, () => Array(9).fill(0));
  fillGrid(solution);
  const puzzle = solution.map((r) => [...r]);
  const cells = shuffled(
    Array.from({ length: 81 }, (_, i) => [Math.floor(i / 9), i % 9])
  );
  let clues = 81;
  for (const [r, c] of cells) {
    if (clues <= target) break;
    const saved = puzzle[r][c];
    puzzle[r][c] = 0;
    const test = puzzle.map((row) => [...row]);
    if (countSolutions(test, 2) !== 1) {
      puzzle[r][c] = saved;
    } else {
      clues--;
    }
  }
  return { puzzle, solution };
}

// ── UI
const LEVELS = [
  { key: "easy", label: "Easy", desc: "~40 clues" },
  { key: "medium", label: "Medium", desc: "~32 clues" },
  { key: "hard", label: "Hard", desc: "~26 clues" },
];

export default function FinderSudokuPage() {
  const [difficulty, setDifficulty] = useState("easy");
  const [seed, setSeed] = useState(0);
  const [given, setGiven] = useState(null);
  const [solution, setSolution] = useState(null);
  const [entries, setEntries] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [checkMode, setCheckMode] = useState(false);

  useEffect(() => {
    document.title = "Sudoku — HustleFinder";
  }, []);

  const regenerate = useCallback(() => {
    const { puzzle, solution: sol } = generatePuzzle(difficulty);
    setGiven(puzzle);
    setSolution(sol);
    setEntries(puzzle.map((r) => [...r]));
    setSelected(null);
    setShowSolution(false);
    setCheckMode(false);
  }, [difficulty]);

  useEffect(() => {
    regenerate();
    // eslint-disable-next-line
  }, [difficulty, seed]);

  const solved = useMemo(() => {
    if (!entries || !solution) return false;
    for (let r = 0; r < 9; r++)
      for (let c = 0; c < 9; c++)
        if (entries[r][c] !== solution[r][c]) return false;
    return true;
  }, [entries, solution]);

  const enterNumber = (v) => {
    if (!selected || !entries || !given) return;
    const [r, c] = selected;
    if (given[r][c] !== 0) return;
    const copy = entries.map((row) => [...row]);
    copy[r][c] = v === copy[r][c] ? 0 : v;
    setEntries(copy);
  };

  if (!given || !entries) {
    return (
      <div data-testid="sudoku-loading" className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Generating puzzle…</div>
      </div>
    );
  }

  return (
    <div
      data-testid="sudoku-page"
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16"
    >
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .sudoku-grid { border: 2px solid #000 !important; }
        }
      `}</style>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="no-print flex items-center justify-between mb-4">
          <Link
            to="/finder/puzzles"
            data-testid="sudoku-back-link"
            className="text-xs text-emerald-700 hover:underline"
          >
            ← back to puzzles
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="sudoku-print-btn"
            className="text-xs bg-slate-900 text-white px-3 py-1.5 rounded-full hover:bg-slate-800"
          >
            Print A4
          </button>
        </div>

        <h1 className="text-3xl font-bold text-slate-900 mb-1">Sudoku</h1>
        <p className="text-sm text-slate-500 mb-5">
          A fresh puzzle every time. Play here or print for the road.
        </p>

        {/* Difficulty picker */}
        <div className="no-print flex gap-2 mb-5" data-testid="sudoku-difficulty-picker">
          {LEVELS.map((l) => (
            <button
              key={l.key}
              onClick={() => setDifficulty(l.key)}
              data-testid={`sudoku-difficulty-${l.key}`}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition ${
                difficulty === l.key
                  ? "bg-emerald-600 text-white"
                  : "bg-white border border-slate-200 text-slate-700 hover:border-emerald-400"
              }`}
            >
              {l.label}
              <div className="text-[10px] opacity-75">{l.desc}</div>
            </button>
          ))}
          <button
            onClick={() => setSeed((s) => s + 1)}
            data-testid="sudoku-new-btn"
            className="py-2 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            title="New puzzle"
          >
            ↻
          </button>
        </div>

        {/* Grid */}
        <div
          className="sudoku-grid mx-auto grid grid-cols-9 bg-white border-2 border-slate-900 select-none"
          style={{ aspectRatio: "1 / 1", maxWidth: "min(96vw, 500px)" }}
          data-testid="sudoku-grid"
        >
          {entries.map((row, r) =>
            row.map((v, c) => {
              const isGiven = given[r][c] !== 0;
              const isSelected =
                selected && selected[0] === r && selected[1] === c;
              const inRowCol =
                selected && (selected[0] === r || selected[1] === c);
              const inBlock =
                selected &&
                Math.floor(selected[0] / 3) === Math.floor(r / 3) &&
                Math.floor(selected[1] / 3) === Math.floor(c / 3);
              const isWrong =
                checkMode && v !== 0 && !isGiven && v !== solution[r][c];
              const borderR = (c + 1) % 3 === 0 && c !== 8;
              const borderB = (r + 1) % 3 === 0 && r !== 8;
              return (
                <button
                  key={`${r}-${c}`}
                  onClick={() => setSelected([r, c])}
                  data-testid={`sudoku-cell-${r}-${c}`}
                  className={`
                    flex items-center justify-center
                    text-lg sm:text-xl font-semibold
                    border-slate-300 border-r border-b
                    ${borderR ? "border-r-2 border-r-slate-900" : ""}
                    ${borderB ? "border-b-2 border-b-slate-900" : ""}
                    ${isSelected ? "bg-emerald-200" : inBlock || inRowCol ? "bg-slate-100" : "bg-white"}
                    ${isGiven ? "text-slate-900" : "text-emerald-700"}
                    ${isWrong ? "text-red-600 bg-red-50" : ""}
                    hover:bg-emerald-50
                  `}
                  style={{ minHeight: 0 }}
                >
                  {v === 0 ? "" : v}
                </button>
              );
            })
          )}
        </div>

        {/* Number pad */}
        <div
          className="no-print grid grid-cols-9 gap-1 mt-4 max-w-md mx-auto"
          data-testid="sudoku-numpad"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((v) => (
            <button
              key={v}
              onClick={() => enterNumber(v)}
              data-testid={`sudoku-num-${v}`}
              className="py-3 rounded-lg bg-white border border-slate-200 text-slate-900 font-bold hover:bg-emerald-50 hover:border-emerald-400"
            >
              {v}
            </button>
          ))}
        </div>

        {/* Controls */}
        <div
          className="no-print flex flex-wrap gap-2 mt-4 max-w-md mx-auto"
          data-testid="sudoku-controls"
        >
          <button
            onClick={() => enterNumber(0)}
            data-testid="sudoku-erase-btn"
            className="flex-1 py-2 px-3 rounded-lg bg-white border border-slate-200 text-sm font-semibold hover:border-red-300 hover:text-red-600"
          >
            Erase
          </button>
          <button
            onClick={() => setCheckMode((m) => !m)}
            data-testid="sudoku-check-btn"
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold ${
              checkMode
                ? "bg-amber-500 text-white"
                : "bg-white border border-slate-200 hover:border-amber-400"
            }`}
          >
            {checkMode ? "Hide errors" : "Check errors"}
          </button>
          <button
            onClick={() => setShowSolution((s) => !s)}
            data-testid="sudoku-solution-btn"
            className="flex-1 py-2 px-3 rounded-lg bg-white border border-slate-200 text-sm font-semibold hover:border-slate-400"
          >
            {showSolution ? "Hide solution" : "Show solution"}
          </button>
        </div>

        {solved && (
          <div
            data-testid="sudoku-solved-banner"
            className="no-print mt-4 max-w-md mx-auto p-4 rounded-2xl bg-emerald-100 border border-emerald-300 text-emerald-800 text-center font-semibold"
          >
            🎉 Solved. Nicely done.
          </div>
        )}

        {showSolution && (
          <div
            data-testid="sudoku-solution"
            className="no-print mt-4 max-w-md mx-auto p-4 rounded-2xl bg-slate-100 text-xs font-mono text-slate-700 text-center"
          >
            {solution.map((row, i) => (
              <div key={i}>{row.join(" ")}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
